import React, { useState } from 'react';

import { path } from 'ramda';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { findIndexFromFieldsArray } from 'components/shared/CRUD/utils/utils';

import { useEditPageContext } from 'components/shared/CRUD/components/InnerPage/EditPageContext';
import { fieldArrayContext } from 'components/shared/CRUD/components/InnerPage/hooks/useFieldArrayContext';
import useRemoveUncommitted from 'components/shared/CRUD/components/InnerPage/hooks/useRemoveUncommitted';

import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import EditCostCenterDetail from './CostCenterDetails/EditCostCenterDetail';
import useColumnDefinitions from './CostCenterDetails/useColumnDefinition';
import {
  type CostCenterDetailPeriod,
  latestChildcareType
} from './CostCenterDetailsTable.utils';

const CostCenterDetailsTable: React.FC<{
  setDeletedIds: React.SetStateAction<any>;
}> = ({ setDeletedIds }) => {
  const { isEditable } = useEditPageContext();
  const { t } = useTranslation('common');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const columns = useColumnDefinitions();

  const { control, getValues } = useFormContext();

  const {
    fields: details,
    append,
    remove,
    update,
    replace
  } = useFieldArray({
    control: control,
    name: 'costCenterDetails'
  });

  const onAddNew = () => {
    // add a new empty record to the fieldArray
    append({
      isCommitted: false,
      flags: {},
      brId: getValues('brId'),
      parentBrDynId: null,
      finBrDynId: null,
      employerId: null,
      // The field array is untyped here, so its entries only declare `id`.
      childcareType: latestChildcareType(
        details as unknown as CostCenterDetailPeriod[]
      ),
      valid: {
        from: null,
        to: null
      }
    });
    setIsDrawerOpen(true);
  };

  const onDelete = (fieldIds) => {
    const index = findIndexFromFieldsArray(fieldIds[0], details);
    const id = path<number>(['brDynId'], details[index]);
    if (id) {
      setDeletedIds((prevState) => [...prevState, id]);
    }
    remove(index);
  };

  useRemoveUncommitted(details, isDrawerOpen, remove);

  const onCloseInnerView = () => {
    setIsDrawerOpen(false);
  };

  return (
    <fieldArrayContext.Provider
      value={{
        fields: details,
        append,
        remove,
        update,
        replace
      }}
    >
      <TableCRUDLayout
        columns={columns}
        rows={details}
        FormComponent={EditCostCenterDetail}
        getRowId={(row) => row.id}
        dynamicHeight={true}
        disableActions={!isEditable}
        onAddClick={onAddNew}
        onDeleteClick={onDelete}
        confirmationTitle={t('deleteDialog.title')}
        confirmationBody={t('deleteDialog.body')}
        onClose={onCloseInnerView}
        shouldRenderToolBar={false}
      />
    </fieldArrayContext.Provider>
  );
};

export default CostCenterDetailsTable;
